import { Injectable } from '@angular/core';
import { LogService } from './log.service';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import {
  DateFilterModel,
  IServerSideGetRowsRequest,
} from '@ag-grid-community/core';
import { ChangeTankFilter } from '../state/actions/filter.action';
import {
  TankQuickFilterModel,
  TankQuickFilterType,
} from '../modules/tank-quick-filters/components/global-tank-filters/tank-quick-filters.component';
import { Observable, firstValueFrom, debounceTime, switchMap, map, startWith, tap } from 'rxjs';
import { IFilterProfile, ITankProfile } from '../interfaces/tankProfile';
import { AppSettings } from '../shared/Constants/sharedConstants';
import {
  selectFilterModel,
  selectGridFilter,
  selectQuickFilterContextChange,
  selectTankContextChanges,
} from '../state/selectors/tank-profile.selectors';
import { TankFiltersOptions } from './tank.service';
import { SetLastSelectedTankQuickFilter } from '../state/actions/user-profile.actions';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { SetFilterModel } from '@ag-grid-enterprise/set-filter';
import { selectLastSelectedTankQuickFilter } from '../state/selectors/user-profile.selectors';
import { format } from 'date-fns';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class TankFilterService {
  private readonly apiURL: string = 'Tank';

  get filterModelState$(): Observable<any> {
    return this.store.select(selectFilterModel);
  }

  constructor(
    private logger: LogService,
    private store: Store<AppState>,
    private restClient: DSRESTClient,
    private translate: TranslateService
  ) {}

  //apply the filter model to the state store gridFilter.filterModel property.
  public applyFilterModelToState(
    filterModel:
      | TAPFilterModelTypes[]
      | TAPFilterModelTypes
      //backwards compatibility for Ag-Grid filter model
      | { [key: string]: any },
    source: string,
    clearQuickFilters: boolean = false
  ) {
    if (!filterModel) return;

    let fullFilterModel = {};

    if (Array.isArray(filterModel) || filterModel?.tapFilterName) {
      //normalize to an
      if (!Array.isArray(filterModel)) {
        filterModel = [filterModel];
      }

      if (filterModel.length === 0) return;

      filterModel.forEach(filter => {
        const { tapFilterName, ...newFilter } = filter; //Reset the filter model to Ag-Grid filter model format.
        fullFilterModel[tapFilterName] = newFilter;
      });

      //build the full filter Model to pass to the state store.
    } else {
      fullFilterModel = filterModel;
    }

    this.store.dispatch(new ChangeTankFilter(fullFilterModel, source ?? 'TankFilterService'));

    if (clearQuickFilters) {
      this.clearLastSelectedTankQuickFilter(false);
    }
  }

  public clearLastSelectedTankQuickFilter(clearFilterModel: boolean = true) {
    this.store.dispatch(new SetLastSelectedTankQuickFilter(null));
    if (clearFilterModel) {
      this.applyFilterModelToState(null, 'TankFilterService');
    }
  }

  public createTankFilterModel<T extends TAPFilterModelTypes>(type: { new (): T }): T {
    return new type();
  }

  public getTankQuickFiltersForFilterBar(
    store: Store<AppState>
  ): Observable<TankQuickFilterModel[]> {
    return store.select(selectQuickFilterContextChange).pipe(
      debounceTime(AppSettings.debounceTime),
      switchMap(value => {
        //Get the current Tank Filter data.
        const tankProfile: Partial<ITankProfile> = {
          tankProfileId: value.tankProfileId,
          includeAffiliatedOrganization: value.includeAffiliatedOrganization,
          includeInactiveTanks: value.includeInactiveTanks,
          includeChildOrganization: value.includeChildOrganization,
          maintenanceTanksOnly: value.maintenanceTanksOnly,
        };
        return this.getTankFilterDataForFiltersBar(value.currentOrgId, tankProfile as ITankProfile).pipe(
          map(data => {
            //setup the tank filter options with the data and return observable of the options
            return TankFiltersOptions.map(f => {
              f = {
                ...f,
                value: data.find(d => d.filterType === f.filterType)?.numberOfMatchingTanks ?? 0,
              };
              return f;
            });
          }),
          startWith(TankFiltersOptions)
        );
      })
    );
  }

  public getGridFilter(): Observable<IServerSideGetRowsRequest> {
    return this.store.select(selectGridFilter);
  }

  public resetLastSelectedTankQuickFilter() {
    //set default Tank Quick Filter - Active Tanks filter
    this.setLastSelectedTankQuickFilter(
      {
        filterType: TankQuickFilterType.ActiveTanks,
        name: 'TankFilters.ActiveTanks',
        value: 0,
        icon: 'active-tanks.svg',
        color: '#00a3e0',
      },
      null,
      'TankFilterService'
    ).then(() => {});
  }

  public getLastSelectedTankQuickFilter(): Observable<TankQuickFilterModel> {
    return this.store.select(selectLastSelectedTankQuickFilter);
  }

  //Toggles the Selected Tank Filter if activeFilter$ is passed in for comparison.
  // If the same filter is selected, it will be unselected....
  public async setLastSelectedTankQuickFilter(
    filter: TankQuickFilterModel,
    activeFilter$: Observable<TankQuickFilterModel | null>,
    source: string
  ): Promise<any> {
    //toggle active Filter
    const activeFilter = activeFilter$ ? await firstValueFrom(activeFilter$) : null;
    if (activeFilter && activeFilter?.filterType === filter.filterType) {
      filter = null;
    }

    //update state store grid filter
    let fm: TAPFilterModelTypes | any = {};
    let filterName = '';

    //setup the filter model based on the filter type.
    switch (filter?.filterType) {
      case TankQuickFilterType.ActiveTanks:
        fm = this.createTankFilterModel(ActiveTankFilterModel);
        filterName = 'Active Tanks';
        break;
      case TankQuickFilterType.NoRecentUpdates:
        fm['noRecentUpdate'] = {
          filter: 'test',
          filterType: 'text',
          type: 'equals',
        };
        filterName = 'No Recent Updates';
        break;
      case TankQuickFilterType.RecentInstalls:
        fm['recentInstall'] = {
          filter: 'test',
          filterType: 'text',
          type: 'equals',
        };

        filterName = 'Recent Installs';
        break;
      case TankQuickFilterType.CriticalLowAlarms:
        fm['volumeAlarmList'] = {
          values: ['Critical Low Alarm'],
          filterType: 'set',
        };
        filterName = 'Critical Low Alarms';
        break;
      case TankQuickFilterType.LowAlarms:
        fm['volumeAlarmList'] = {
          values: ['Low Alarm'],
          filterType: 'set',
        };
        filterName = 'Low Alarms';
        break;
      case TankQuickFilterType.LowBattery:
        fm['maintAlarmList'] = {
          values: ['Low Battery Alarm'],
          filterType: 'set',
        };
        filterName = 'Low Battery Alarms';
        break;
      case TankQuickFilterType.ReorderAlarms:
        fm['volumeAlarmList'] = {
          values: ['Reorder Alarm'],
          filterType: 'set',
        };
        filterName = 'Reorder Alarms';
        break;
      case TankQuickFilterType.SafetyStockAlarms:
        fm['volumeAlarmList'] = {
          values: ['Safety Stock Alarm'],
          filterType: 'set',
        };
        filterName = 'Safety Stock Alarms';
        break;
      case TankQuickFilterType.LeastEfficientDeliveries:
        fm['leastEfficientDeliveries'] = {
          filter: 'test',
          filterType: 'text',
          type: 'equals',
        };
        filterName = 'Least Efficient Deliveries';
        break;
      case TankQuickFilterType.NeedsDelivery:
        fm['needsDelivery'] = {
          filter: 'test',
          filterType: 'text',
          type: 'equals',
        };
        filterName = 'Needs Delivery';
        break;
      case TankQuickFilterType.ReceivingLocations:
        fm['isLocationShipTo'] = {
          filterType: 'set',
          values: ['true'],
        };
        filterName = 'Receiving Locations';
        break;
      default:
        fm = null;
        break;
    }

    //this.store.dispatch(new ChangeFilterProfile(null));
    this.store.dispatch(new SetLastSelectedTankQuickFilter(filter));
    fm = fm ?? {};
    this.applyFilterModelToState(fm, source);
  }

  public processGlobalTankSearch(
    filterModel: { [key: string]: any } | TAPFilterModelTypes
  ): { [key: string]: any } | TAPFilterModelTypes {
    //strip out other filters if global search is present.
    const gtkey: string = 'globalTankSearch';
    if (gtkey in filterModel) {
      return { [gtkey]: (filterModel as { [key: string]: any })[gtkey] };
    }
    return filterModel;
  }

  //Get the data for the Tank Filters for the Filter Bar from the API
  private getTankFilterDataForFiltersBar(
    orgID: number,
    tankProfile: ITankProfile
  ): Observable<any> {
    //remove the grid columns from the tank profile for the API call.
    tankProfile = { ...tankProfile, tankGridColumns: [] };
    const url = `${this.apiURL}/GetTankFiltersForFiltersBar/${orgID}`;
    const apiResponse = this.restClient.post(url, tankProfile);

    return apiResponse.pipe(
      tap(response => {
        this.logger.log('Tank/getTankFilterDataForFiltersBar Response', response);
      })
    );
  }
}

/***
 * Supporting Classes for Tank Filtesr
 */

interface TAPFilterModelBase {
  readonly tapFilterName;
}

export enum TapFilterNames {
  LastMovedOn = 'localized_LastMovedOn',
  ActiveTanks = 'active',
  Product = 'prodDesc',
  Location = 'locationName',
  Geolocation = 'geoLocationFilter',
}

export class LastMovedOnFilterModel implements DateFilterModel, TAPFilterModelBase {
  readonly tapFilterName = TapFilterNames.LastMovedOn;
  dateFrom: string;
  dateTo: string;
  filterType? = 'date';
  type? = 'equals';

  constructor() {
    const dt = new Date();
    const dtFormat = 'yyyy-MM-dd 00:00:00';
    const formatedDt = format(dt, dtFormat); //formatDate(dt,'MM/dd/yyyy hh:mm:ss', 'en-US');

    this.dateFrom = formatedDt;
    this.dateTo = null;

    //console.log('formattedDate', dt, dtFormat, formatedDt);
  }
}

export class LocationFilterModel implements TAPFilterModelBase, SetFilterModel {
  readonly tapFilterName = TapFilterNames.Location;
  filterType = 'set';
  values = [];
}

export class ActiveTankFilterModel implements TAPFilterModelBase, SetFilterModel {
  readonly tapFilterName = TapFilterNames.ActiveTanks;
  filterType = 'set';
  values = ['Active'];
}

export class ProductFilterModel implements TAPFilterModelBase, SetFilterModel {
  readonly tapFilterName = TapFilterNames.Product;
  filterType = 'set';
  values = [];
}
export class GeolocationFilterModel implements TAPFilterModelBase, SetFilterModel {
  readonly tapFilterName = TapFilterNames.Geolocation;
  filterType = 'set';
  values = [];
}

export type TAPFilterModelTypes =
  | LastMovedOnFilterModel
  | ActiveTankFilterModel
  | ProductFilterModel
  | LocationFilterModel
  | GeolocationFilterModel;
